import { useState, type ReactNode, type SubmitEvent } from 'react';
import { Combobox, Input, Textarea, type SelectOption } from '../atoms';
import { ruleValue, validateFields, type ValidationRule } from '../../lib/forms';
import { FormActions, FormField } from '../molecules';

export type FormFieldType = 'text' | 'email' | 'password' | 'number' | 'date' | 'textarea' | 'select';

export interface FormFieldConfig<TValues extends Record<string, string>> {
  name: keyof TValues & string;
  label: string;
  type?: FormFieldType;
  placeholder?: string;
  hint?: ReactNode;
  /** Required when type is 'select'. */
  options?: SelectOption[];
  /** Rules this field is checked against on blur and on submit; `required` also marks the label. */
  validation?: ValidationRule<TValues>;
}

interface FormBuilderProps<TValues extends Record<string, string>> {
  fields: FormFieldConfig<TValues>[];
  values: TValues;
  /** Server-side or other external errors; shown regardless of touched state, and take priority over validation. */
  errors?: Partial<Record<keyof TValues, string>>;
  onChange: (name: keyof TValues, value: string) => void;
  /** Called only once every field has passed its validation. */
  onSubmit: (event: SubmitEvent<HTMLFormElement>) => void;
  /** Shown above the actions, e.g. a failed-submit message. */
  formError?: ReactNode;
  submitLabel?: string;
  busyLabel?: string;
  cancelLabel?: string;
  onCancel?: () => void;
  busy?: boolean;
}

/** Renders a field schema as a validated form: any module describes its fields and rules instead of hand-wiring FormField/Input pairs. */
export function FormBuilder<TValues extends Record<string, string>>({
  fields,
  values,
  errors,
  onChange,
  onSubmit,
  formError,
  submitLabel,
  busyLabel,
  cancelLabel,
  onCancel,
  busy,
}: FormBuilderProps<TValues>) {
  const [touched, setTouched] = useState<Partial<Record<keyof TValues, boolean>>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const validationErrors = validateFields(fields, values);

  function markTouched(name: keyof TValues) {
    setTouched((t) => ({ ...t, [name]: true }));
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitAttempted(true);
    if (Object.keys(validationErrors).length > 0) return;
    onSubmit(event);
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
      {fields.map((field) => {
        const error = errors?.[field.name] ?? ((touched[field.name] || submitAttempted) ? validationErrors[field.name] : undefined);
        const value = values[field.name] ?? '';
        const required = Boolean(field.validation?.required);
        const minLength = ruleValue(field.validation?.minLength);
        const maxLength = ruleValue(field.validation?.maxLength);
        const min = ruleValue(field.validation?.min);
        const max = ruleValue(field.validation?.max);
        const pattern = ruleValue(field.validation?.pattern);

        return (
          <FormField key={field.name} label={field.label} required={required} hint={field.hint} error={error}>
            {field.type === 'textarea' ? (
              <Textarea
                value={value}
                placeholder={field.placeholder}
                required={required}
                minLength={minLength}
                maxLength={maxLength}
                onChange={(e) => onChange(field.name, e.target.value)}
                onBlur={() => markTouched(field.name)}
              />
            ) : field.type === 'select' ? (
              <Combobox
                options={field.options ?? []}
                value={value}
                placeholder={field.placeholder}
                onChange={(v) => onChange(field.name, v)}
                onBlur={() => markTouched(field.name)}
              />
            ) : (
              <Input
                type={field.type ?? 'text'}
                value={value}
                placeholder={field.placeholder}
                required={required}
                minLength={minLength}
                maxLength={maxLength}
                min={min}
                max={max}
                pattern={pattern?.source}
                onChange={(e) => onChange(field.name, e.target.value)}
                onBlur={() => markTouched(field.name)}
              />
            )}
          </FormField>
        );
      })}

      {formError && (
        <p role="alert" className="rounded-lg bg-chip-critical-bg px-3 py-2 text-[13px] font-medium text-chip-critical-fg">
          {formError}
        </p>
      )}

      <FormActions submitLabel={submitLabel} busyLabel={busyLabel} cancelLabel={cancelLabel} onCancel={onCancel} busy={busy} />
    </form>
  );
}
